/* tslint:disable */
/* eslint-disable */
/**
 * Scrivio Users API
 * Scrivio API for user profile management.  ## Authentication All endpoints require Firebase Authentication JWT token in `Authorization: Bearer <token>` header.  ## Error Responses All error responses follow RFC 7807 Problem Details format. 
 *
 * The version of the OpenAPI document: 1.0.0
 * Contact: support@scrivio.nl
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */


import type { Configuration } from './configuration';
import type { AxiosPromise, AxiosInstance, RawAxiosRequestConfig } from 'axios';
import globalAxios from 'axios';
// Some imports not used depending on template conditions
// @ts-ignore
import { DUMMY_BASE_URL, assertParamExists, setApiKeyToObject, setBasicAuthToObject, setBearerAuthToObject, setOAuthToObject, setSearchParams, serializeDataIfNeeded, toPathString, createRequestFunction } from './common';
import type { RequestArgs } from './base';
// @ts-ignore
import { BASE_PATH, COLLECTION_FORMATS, BaseAPI, RequiredError, operationServerMap } from './base';

/**
 * A date-range override of a user\'s default working hours.
 */
export interface AvailabilityException {
    'id': string;
    /**
     * The user this exception belongs to
     */
    'userId': string;
    /**
     * First day the exception applies to (inclusive)
     */
    'startDate': string;
    /**
     * Last day the exception applies to (inclusive). Must be on or after startDate.
     */
    'endDate': string;
    'type': AvailabilityExceptionType;
    /**
     * Custom block start (local time, HH:mm). Required for CUSTOM_HOURS, null for UNAVAILABLE.
     */
    'startTime'?: string | null;
    /**
     * Custom block end (local time, HH:mm). Required for CUSTOM_HOURS, null for UNAVAILABLE.
     */
    'endTime'?: string | null;
    /**
     * Optional free-text note (e.g. \"vakantie\")
     */
    'note'?: string | null;
}


export interface AvailabilityExceptionListResponse {
    'exceptions': Array<AvailabilityException>;
}
/**
 * Create or replace an availability exception.
 */
export interface AvailabilityExceptionRequest {
    /**
     * First day the exception applies to (inclusive)
     */
    'startDate': string;
    /**
     * Last day the exception applies to (inclusive). Must be on or after startDate.
     */
    'endDate': string;
    'type': AvailabilityExceptionType;
    /**
     * Custom block start (local time, HH:mm). Required for CUSTOM_HOURS, null for UNAVAILABLE.
     */
    'startTime'?: string | null;
    /**
     * Custom block end (local time, HH:mm). Required for CUSTOM_HOURS, null for UNAVAILABLE.
     */
    'endTime'?: string | null;
    /**
     * Optional free-text note
     */
    'note'?: string | null;
}


/**
 * The kind of availability exception. `UNAVAILABLE` marks a full absence (holiday, sickness); `CUSTOM_HOURS` overrides the working blocks for the date range with `startTime`/`endTime`. 
 */

export const AvailabilityExceptionType = {
    Unavailable: 'UNAVAILABLE',
    CustomHours: 'CUSTOM_HOURS'
} as const;

export type AvailabilityExceptionType = typeof AvailabilityExceptionType[keyof typeof AvailabilityExceptionType];


/**
 * One entry of the behandelaar roster (`GET /api/v1/users/behandelaars`) — everything a picker needs to name a colleague and nothing else.  Every field a `UserProfile` carries that only user administration needs is deliberately absent, which is what lets this be read on `clients.read`. `isBehandelaar` and `isActive` are absent for a different reason: the roster is *defined* as the active designated behandelaars, so both would be constantly `true` and a consumer filtering on them would read as if some other answer were possible. 
 */
export interface BehandelaarSummary {
    /**
     * Unique user identifier
     */
    'id': string;
    /**
     * User first name
     */
    'firstName': string;
    /**
     * User last name
     */
    'lastName': string;
    /**
     * Name prefix (tussenvoegsel), which belongs between the two names
     */
    'prefix'?: string | null;
    /**
     * Job title/function, shown beside the name to tell colleagues apart
     */
    'functionTitle'?: string | null;
    /**
     * Whether the organization has also designated this behandelaar as regiebehandelaar (#3115), which is what narrows a zorgtraject\'s regiebehandelaar picker. 
     */
    'isRegiebehandelaar': boolean;
}
export interface BeroepZorgverlener {
    /**
     * The CL0001-VEKT code, as stored in `beroepZorgverlener`
     */
    'code': string;
    /**
     * Vektis\'s own wording for the beroep, shown in the picker
     */
    'betekenis': string;
}
/**
 * Input type of `UserService.createUser`. No operation accepts this as a request body: `POST /api/v1/users` was removed in #3068 because it produced a user that could never sign in. Onboarding runs through the invitation flow.
 */
export interface CreateUserRequest {
    /**
     * User email address
     */
    'email': string;
    /**
     * User first name
     */
    'firstName': string;
    /**
     * User last name
     */
    'lastName': string;
    /**
     * Name prefix
     */
    'prefix'?: string | null;
    /**
     * Phone number
     */
    'phone'?: string | null;
    /**
     * Job title/function
     */
    'functionTitle'?: string | null;
    /**
     * AGB code - 8-digit healthcare provider identifier
     */
    'agbCode'?: string | null;
    /**
     * BIG registration number
     */
    'bigRegistration'?: string | null;
    /**
     * Whether the user account is active
     */
    'isActive'?: boolean;
    /**
     * Whether the user is a behandelaar (treatment provider)
     */
    'isBehandelaar'?: boolean;
    /**
     * Whether the user is designated as regiebehandelaar. Ignored when `isBehandelaar` is false. 
     */
    'isRegiebehandelaar'?: boolean;
    /**
     * NZA profession code; null when the user has none
     */
    'professionCode'?: ProfessionCode | null;
}


export interface CurrentUserPermissions {
    /**
     * Permission keys from `permission-bits.json`, e.g. `clients.read`
     */
    'permissions': Array<string>;
}
/**
 * Resolved free/busy availability for a single calendar day.
 */
export interface DayAvailability {
    'date': string;
    'weekday': Weekday;
    /**
     * True when the user is scheduled to work this day, regardless of bookings. A user who configured working hours is scheduled on the weekdays those hours cover, minus full-day absences; a user with no configured working hours at all has no availability baseline and counts as scheduled every day, so an organization that never filled in werktijden does not read as permanently unavailable. Blocks of either bookability count: an \"in house\" block still means the user is present. 
     */
    'working': boolean;
    /**
     * True when the user has at least one free, client-bookable block on this day.
     */
    'available': boolean;
    /**
     * Free blocks after resolving the weekday\'s client-bookable working hours, subtracting availability exceptions, and subtracting booked appointments. `IN_HOUSE` working blocks are never included — the user is present, but not bookable for clients. 
     */
    'availableBlocks': Array<TimeBlock>;
    /**
     * Spans taken by booked, availability-blocking appointments, merged into ascending non-overlapping blocks. Lets a caller tell \"already booked\" apart from \"outside the working hours\" — both are missing from `availableBlocks`, but only the first is *bezet*. 
     */
    'busyBlocks': Array<TimeBlock>;
    /**
     * The day\'s working blocks marked `IN_HOUSE`: the user is present but not bookable for clients. Missing from `availableBlocks` like booked and out-of-hours time, and listed separately so a planner can say \"aanwezig, niet inplanbaar\" instead of \"buiten werktijden\". 
     */
    'inHouseBlocks': Array<TimeBlock>;
}


/**
 * User\'s preferred language for the UI. New users start on `NL`; that default lives on the `users.language` database column.  No schema `default:` on purpose (#3213) — a default becomes a pre-initialised request-DTO field, turning a partial update that omits this property into an explicit \"set to NL\". 
 */

export const Language = {
    Nl: 'NL',
    En: 'EN'
} as const;

export type Language = typeof Language[keyof typeof Language];


export interface ProblemDetail {
    /**
     * A URI reference that identifies the problem type
     */
    'type': string;
    /**
     * A short, human-readable summary of the problem
     */
    'title': string;
    /**
     * The HTTP status code
     */
    'status': number;
    /**
     * A human-readable explanation specific to this occurrence
     */
    'detail'?: string;
    /**
     * A URI reference that identifies the specific occurrence
     */
    'instance'?: string;
    /**
     * List of validation errors
     */
    'errors'?: Array<ProblemDetailErrorsInner>;
}
export interface ProblemDetailErrorsInner {
    'field'?: string;
    'message'?: string;
}
/**
 * NZA profession code for billing service code lookup
 */

export const ProfessionCode = {
    ArtsBig3: 'ARTS_BIG3',
    ArtsBig14: 'ARTS_BIG14',
    GzPsych: 'GZ_PSYCH',
    KlinPsych: 'KLIN_PSYCH',
    Psychother: 'PSYCHOTHER',
    Verpl: 'VERPL',
    VerplSpec: 'VERPL_SPEC',
    Overig: 'OVERIG'
} as const;

export type ProfessionCode = typeof ProfessionCode[keyof typeof ProfessionCode];


export interface SwitchOrganizationRequest {
    /**
     * The ID of the organization to switch to
     */
    'organizationId': string;
}
export interface SwitchOrganizationResponse {
    /**
     * The new active organization ID
     */
    'organizationId': string;
    /**
     * The name of the new active organization
     */
    'organizationName': string;
    /**
     * Instructions for the client
     */
    'message': string;
}
export interface SyncFirebaseClaims200Response {
    'message'?: string;
    'organizationId'?: string | null;
    'permissionsCount'?: number;
}
/**
 * A contiguous block of time within a single day, as local wall-clock times.
 */
export interface TimeBlock {
    /**
     * Block start (local time, HH:mm, 24-hour)
     */
    'startTime': string;
    /**
     * Block end (local time, HH:mm, 24-hour). Must be after startTime.
     */
    'endTime': string;
}
export interface UpdateUserProfileRequest {
    /**
     * User first name
     */
    'firstName'?: string;
    /**
     * User last name
     */
    'lastName'?: string;
    /**
     * Name prefix
     */
    'prefix'?: string | null;
    /**
     * Phone number
     */
    'phone'?: string | null;
    /**
     * URL to user avatar image
     */
    'avatarUrl'?: string | null;
    /**
     * Job title/function
     */
    'functionTitle'?: string | null;
    /**
     * AGB code - 8-digit healthcare provider identifier
     */
    'agbCode'?: string | null;
    /**
     * BIG registration number
     */
    'bigRegistration'?: string | null;
    /**
     * Primary location identifier
     */
    'primaryLocationId'?: string | null;
    /**
     * User preferences as JSON object
     */
    'preferences'?: { [key: string]: any; } | null;
    /**
     * Whether the membership in the acting organisation is active. Setting it to false also clears `isBehandelaar` and `isRegiebehandelaar`, and is refused with a 409 when it would leave the organisation without an enabled user administrator, or when the membership is your own. 
     */
    'isActive'?: boolean;
    /**
     * Whether the user is a behandelaar (treatment provider). Never stored on a membership that is being disabled in the same request. 
     */
    'isBehandelaar'?: boolean;
    /**
     * Whether the user is designated as regiebehandelaar. Setting `isBehandelaar` to false also clears this designation, as does disabling the membership. 
     */
    'isRegiebehandelaar'?: boolean;
    /**
     * NZA profession code; null when the user has none
     */
    'professionCode'?: ProfessionCode | null;
    /**
     * Vektis CL0001-VEKT beroep zorgverlener. Validated against the committed publication on write; a code the list does not contain is a 400. See `GET /api/v1/users/beroepen-zorgverlener` for the values. 
     */
    'beroepZorgverlener'?: string | null;
    'language'?: Language;
}


/**
 * Full replacement of the weekly pattern. Weekdays omitted from `weekdays` are treated as non-working days. 
 */
export interface UpdateWorkingHoursRequest {
    /**
     * Working blocks per weekday. At most one entry per weekday.
     */
    'weekdays': Array<WorkingHoursDay>;
}
/**
 * Resolved availability for a user over a date range.
 */
export interface UserAvailabilityResponse {
    'userId': string;
    'startDate': string;
    'endDate': string;
    /**
     * One entry per day in [startDate, endDate], ordered ascending.
     */
    'days': Array<DayAvailability>;
}
export interface UserListResponse {
    /**
     * List of users
     */
    'content': Array<UserProfile>;
    /**
     * Current page number (0-indexed)
     */
    'page': number;
    /**
     * Page size
     */
    'size': number;
    /**
     * Total number of users
     */
    'totalElements': number;
    /**
     * Total number of pages
     */
    'totalPages': number;
}
export interface UserOrganizationSummary {
    'id': string;
    'name': string;
    'slug': string;
    /**
     * Whether this is the user\'s currently active organization
     */
    'isActive': boolean;
}
export interface UserOrganizationsResponse {
    'organizations': Array<UserOrganizationSummary>;
    /**
     * The currently active organization ID
     */
    'activeOrganizationId': string;
}
export interface UserProfile {
    /**
     * Unique user identifier
     */
    'id': string;
    /**
     * Organization identifier (null if user not assigned to an organization)
     */
    'organizationId'?: string | null;
    /**
     * Display name of the organization in `organizationId`. Null when the user has no organization. Carried on the profile so a consumer needs no second round trip to turn the id into something readable — the frontend tags Sentry events with it so a production report names the practice it came from (#3598). 
     */
    'organizationName'?: string | null;
    /**
     * User email address
     */
    'email': string;
    /**
     * User first name
     */
    'firstName': string;
    /**
     * User last name
     */
    'lastName': string;
    /**
     * Name prefix (e.g., \"van der\", \"de\")
     */
    'prefix'?: string | null;
    /**
     * Phone number
     */
    'phone'?: string | null;
    /**
     * URL to user avatar image
     */
    'avatarUrl'?: string | null;
    /**
     * Job title/function
     */
    'functionTitle'?: string | null;
    /**
     * AGB code - 8-digit healthcare provider identifier (Algemeen GegevensBeheer)
     */
    'agbCode'?: string | null;
    /**
     * BIG registration number (Beroepen in de Individuele Gezondheidszorg)
     */
    'bigRegistration'?: string | null;
    /**
     * Primary location/office identifier
     */
    'primaryLocationId'?: string | null;
    /**
     * Whether the user account is active
     */
    'isActive': boolean;
    /**
     * Whether the user is a behandelaar (treatment provider)
     */
    'isBehandelaar': boolean;
    /**
     * Whether the organization has designated the user as regiebehandelaar, making them selectable as regiebehandelaar on a zorgtraject. Refines `isBehandelaar` and grants no permissions. 
     */
    'isRegiebehandelaar': boolean;
    /**
     * NZA profession code; null when the user has none
     */
    'professionCode'?: ProfessionCode | null;
    /**
     * Vektis CL0001-VEKT beroep zorgverlener — what a declaration says about a behandelaar whom no AGB code identifies (VC068). Required only when `agbCode` is absent; null otherwise. The valid values are the code list itself, served by `GET /api/v1/users/beroepen-zorgverlener`, and are deliberately not enumerated here — a copy of a Vektis publication is a copy that drifts. 
     */
    'beroepZorgverlener'?: string | null;
    /**
     * User preferences as JSON object
     */
    'preferences'?: { [key: string]: any; };
    /**
     * Creation timestamp in milliseconds
     */
    'createdAt': number;
    /**
     * Last update timestamp in milliseconds
     */
    'updatedAt': number;
    /**
     * Whether the user\'s email is verified
     */
    'emailVerified'?: boolean;
    /**
     * Timestamp when email was verified (milliseconds)
     */
    'emailVerifiedAt'?: number | null;
    /**
     * Whether MFA is enabled for the user
     */
    'mfaEnabled'?: boolean;
    /**
     * Timestamp when MFA was enrolled (milliseconds)
     */
    'mfaEnrolledAt'?: number | null;
    /**
     * When this user last authenticated (milliseconds), or null when they never have. Response-only: nothing writes it through this contract.  It is last **authentication**, not last activity. `UserSignInService` rewrites the column at most once per throttle window (#2330), so a user with a live session moves it on token-refresh boundaries. That is enough to answer \"has not signed in since\", and wrong for \"was active today\".  It is also per **user**, not per membership: someone working at two practices has one timestamp, and neither practice can tell whether the idleness is theirs. A per-organisation activity signal would have to come from `audit_log` instead. 
     */
    'lastSignedInAt'?: number | null;
    'language': Language;
}


/**
 * Resolved availability for a set of users over a shared date range. Holds one [UserAvailabilityResponse] per requested user that belongs to the current organization; ids that do not resolve are omitted, so callers must key on `userId` rather than assume positional alignment. 
 */
export interface UsersAvailabilityResponse {
    /**
     * One entry per resolved user, ordered as requested.
     */
    'users': Array<UserAvailabilityResponse>;
}
/**
 * Weekly working-hours patterns for a set of users. Holds one [WorkingHours] per requested user that belongs to the current organization; ids that do not resolve are omitted, so callers must key on `userId` rather than assume positional alignment. 
 */
export interface UsersWorkingHoursResponse {
    /**
     * One entry per resolved user, ordered as requested.
     */
    'users': Array<WorkingHours>;
}
/**
 * Day of the week (matches java.time.DayOfWeek names)
 */

export const Weekday = {
    Monday: 'MONDAY',
    Tuesday: 'TUESDAY',
    Wednesday: 'WEDNESDAY',
    Thursday: 'THURSDAY',
    Friday: 'FRIDAY',
    Saturday: 'SATURDAY',
    Sunday: 'SUNDAY'
} as const;

export type Weekday = typeof Weekday[keyof typeof Weekday];


/**
 * A working-hours block: a contiguous span of local wall-clock time plus the bookability it was scheduled with. 
 */
export interface WorkingBlock {
    /**
     * Block start (local time, HH:mm, 24-hour)
     */
    'startTime': string;
    /**
     * Block end (local time, HH:mm, 24-hour). Must be after startTime.
     */
    'endTime': string;
    /**
     * Bookability of the block. Always present on responses; omitting it on a write means `CLIENT_BOOKABLE`, so a client that predates the field keeps writing ordinary working hours. 
     */
    'type'?: WorkingHoursBlockType;
    /**
     * Optional short name of the block (e.g. \"Flex\", \"SO\"), printed at the top of the block in the agenda. Display-only: `type` alone decides bookability. A blank label is stored as none. 
     */
    'label'?: string;
    /**
     * Optional `#RRGGBB` background tint the agenda draws the block with. Display-only. A block with neither label nor colour is drawn as plain working hours. 
     */
    'color'?: string;
}


/**
 * A user\'s weekly recurring working-hours pattern within an organization.
 */
export interface WorkingHours {
    /**
     * The user this pattern belongs to
     */
    'userId': string;
    /**
     * The organization this pattern applies to
     */
    'organizationId': string;
    /**
     * One entry per weekday MONDAY..SUNDAY (always all seven).
     */
    'weekdays': Array<WorkingHoursDay>;
}
/**
 * Whether a working block may be filled with client appointments. `CLIENT_BOOKABLE` is normal working time; `IN_HOUSE` marks time the user is present but not bookable for clients (MDO, intern overleg, administratie) — it counts as working, is never offered as free time, and booking into it warns rather than blocks. 
 */

export const WorkingHoursBlockType = {
    ClientBookable: 'CLIENT_BOOKABLE',
    InHouse: 'IN_HOUSE'
} as const;

export type WorkingHoursBlockType = typeof WorkingHoursBlockType[keyof typeof WorkingHoursBlockType];


/**
 * The working blocks for a single weekday. An empty `blocks` list means the user does not work that day; more than one block models a split shift (e.g. a lunch break) or a mix of client-bookable and \"in house\" time. 
 */
export interface WorkingHoursDay {
    'weekday': Weekday;
    /**
     * Zero or more non-overlapping time blocks, ordered by startTime.
     */
    'blocks': Array<WorkingBlock>;
}



/**
 * AvailabilityApi - axios parameter creator
 */
export const AvailabilityApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * Resolves the authenticated user\'s free/busy availability over a date range: the working blocks for each weekday, minus availability exceptions, minus booked appointments. When `endDate` is omitted a single day (`startDate`) is returned. 
         * @summary Get current user\'s resolved availability
         * @param {string} startDate First day to resolve availability for
         * @param {string} [endDate] Last day to resolve availability for (inclusive). Defaults to startDate.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getMyAvailability: async (startDate: string, endDate?: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'startDate' is not null or undefined
            assertParamExists('getMyAvailability', 'startDate', startDate)
            const localVarPath = `/api/v1/users/me/availability`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (startDate !== undefined) {
                localVarQueryParameter['startDate'] = (startDate as any instanceof Date) ?
                    (startDate as any).toISOString().substring(0,10) :
                    startDate;
            }

            if (endDate !== undefined) {
                localVarQueryParameter['endDate'] = (endDate as any instanceof Date) ?
                    (endDate as any).toISOString().substring(0,10) :
                    endDate;
            }

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Resolves another user\'s free/busy availability over a date range within the current organization. See `getMyAvailability` for the resolution rules.  Reading a colleague\'s availability is a scheduling action, not user administration: it requires `appointments.read` (the permission that gates the combined agenda) or `users.read`. 
         * @summary Get a user\'s resolved availability
         * @param {string} userId User ID
         * @param {string} startDate First day to resolve availability for
         * @param {string} [endDate] Last day to resolve availability for (inclusive). Defaults to startDate.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getUserAvailability: async (userId: string, startDate: string, endDate?: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'userId' is not null or undefined
            assertParamExists('getUserAvailability', 'userId', userId)
            // verify required parameter 'startDate' is not null or undefined
            assertParamExists('getUserAvailability', 'startDate', startDate)
            const localVarPath = `/api/v1/users/{userId}/availability`
                .replace(`{${"userId"}}`, encodeURIComponent(String(userId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (startDate !== undefined) {
                localVarQueryParameter['startDate'] = (startDate as any instanceof Date) ?
                    (startDate as any).toISOString().substring(0,10) :
                    startDate;
            }

            if (endDate !== undefined) {
                localVarQueryParameter['endDate'] = (endDate as any instanceof Date) ?
                    (endDate as any).toISOString().substring(0,10) :
                    endDate;
            }

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Resolves the free/busy availability of every user in `userIds` over a date range within the current organization, in a single request. Backs the combined agenda\'s \"aanwezig vandaag\" filter and the appointment slot-availability hints, which previously fired one request per roster member. See `getUserAvailability` for the per-day resolution rules.  Reading colleagues\' availability is a scheduling action, not user administration: it requires `appointments.read` (the permission that gates the combined agenda) or `users.read`. Ids that are not members of the current organization are silently omitted from the response rather than failing the whole request. 
         * @summary Get resolved availability for several users at once
         * @param {Array<string>} userIds The users to resolve for. Repeat the parameter for each id (&#x60;?userIds&#x3D;a&amp;userIds&#x3D;b&#x60;). Ids outside the current organization are omitted from the response. 
         * @param {string} startDate First day to resolve availability for
         * @param {string} [endDate] Last day to resolve availability for (inclusive). Defaults to startDate.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getUsersAvailability: async (userIds: Array<string>, startDate: string, endDate?: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'userIds' is not null or undefined
            assertParamExists('getUsersAvailability', 'userIds', userIds)
            // verify required parameter 'startDate' is not null or undefined
            assertParamExists('getUsersAvailability', 'startDate', startDate)
            const localVarPath = `/api/v1/users/availability`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (userIds) {
                localVarQueryParameter['userIds'] = userIds;
            }

            if (startDate !== undefined) {
                localVarQueryParameter['startDate'] = (startDate as any instanceof Date) ?
                    (startDate as any).toISOString().substring(0,10) :
                    startDate;
            }

            if (endDate !== undefined) {
                localVarQueryParameter['endDate'] = (endDate as any instanceof Date) ?
                    (endDate as any).toISOString().substring(0,10) :
                    endDate;
            }

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * AvailabilityApi - functional programming interface
 */
export const AvailabilityApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = AvailabilityApiAxiosParamCreator(configuration)
    return {
        /**
         * Resolves the authenticated user\'s free/busy availability over a date range: the working blocks for each weekday, minus availability exceptions, minus booked appointments. When `endDate` is omitted a single day (`startDate`) is returned. 
         * @summary Get current user\'s resolved availability
         * @param {string} startDate First day to resolve availability for
         * @param {string} [endDate] Last day to resolve availability for (inclusive). Defaults to startDate.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getMyAvailability(startDate: string, endDate?: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<UserAvailabilityResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getMyAvailability(startDate, endDate, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AvailabilityApi.getMyAvailability']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Resolves another user\'s free/busy availability over a date range within the current organization. See `getMyAvailability` for the resolution rules.  Reading a colleague\'s availability is a scheduling action, not user administration: it requires `appointments.read` (the permission that gates the combined agenda) or `users.read`. 
         * @summary Get a user\'s resolved availability
         * @param {string} userId User ID
         * @param {string} startDate First day to resolve availability for
         * @param {string} [endDate] Last day to resolve availability for (inclusive). Defaults to startDate.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getUserAvailability(userId: string, startDate: string, endDate?: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<UserAvailabilityResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getUserAvailability(userId, startDate, endDate, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AvailabilityApi.getUserAvailability']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Resolves the free/busy availability of every user in `userIds` over a date range within the current organization, in a single request. Backs the combined agenda\'s \"aanwezig vandaag\" filter and the appointment slot-availability hints, which previously fired one request per roster member. See `getUserAvailability` for the per-day resolution rules.  Reading colleagues\' availability is a scheduling action, not user administration: it requires `appointments.read` (the permission that gates the combined agenda) or `users.read`. Ids that are not members of the current organization are silently omitted from the response rather than failing the whole request. 
         * @summary Get resolved availability for several users at once
         * @param {Array<string>} userIds The users to resolve for. Repeat the parameter for each id (&#x60;?userIds&#x3D;a&amp;userIds&#x3D;b&#x60;). Ids outside the current organization are omitted from the response. 
         * @param {string} startDate First day to resolve availability for
         * @param {string} [endDate] Last day to resolve availability for (inclusive). Defaults to startDate.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getUsersAvailability(userIds: Array<string>, startDate: string, endDate?: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<UsersAvailabilityResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getUsersAvailability(userIds, startDate, endDate, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AvailabilityApi.getUsersAvailability']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * AvailabilityApi - factory interface
 */
export const AvailabilityApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = AvailabilityApiFp(configuration)
    return {
        /**
         * Resolves the authenticated user\'s free/busy availability over a date range: the working blocks for each weekday, minus availability exceptions, minus booked appointments. When `endDate` is omitted a single day (`startDate`) is returned. 
         * @summary Get current user\'s resolved availability
         * @param {string} startDate First day to resolve availability for
         * @param {string} [endDate] Last day to resolve availability for (inclusive). Defaults to startDate.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getMyAvailability(startDate: string, endDate?: string, options?: RawAxiosRequestConfig): AxiosPromise<UserAvailabilityResponse> {
            return localVarFp.getMyAvailability(startDate, endDate, options).then((request) => request(axios, basePath));
        },
        /**
         * Resolves another user\'s free/busy availability over a date range within the current organization. See `getMyAvailability` for the resolution rules.  Reading a colleague\'s availability is a scheduling action, not user administration: it requires `appointments.read` (the permission that gates the combined agenda) or `users.read`. 
         * @summary Get a user\'s resolved availability
         * @param {string} userId User ID
         * @param {string} startDate First day to resolve availability for
         * @param {string} [endDate] Last day to resolve availability for (inclusive). Defaults to startDate.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getUserAvailability(userId: string, startDate: string, endDate?: string, options?: RawAxiosRequestConfig): AxiosPromise<UserAvailabilityResponse> {
            return localVarFp.getUserAvailability(userId, startDate, endDate, options).then((request) => request(axios, basePath));
        },
        /**
         * Resolves the free/busy availability of every user in `userIds` over a date range within the current organization, in a single request. Backs the combined agenda\'s \"aanwezig vandaag\" filter and the appointment slot-availability hints, which previously fired one request per roster member. See `getUserAvailability` for the per-day resolution rules.  Reading colleagues\' availability is a scheduling action, not user administration: it requires `appointments.read` (the permission that gates the combined agenda) or `users.read`. Ids that are not members of the current organization are silently omitted from the response rather than failing the whole request. 
         * @summary Get resolved availability for several users at once
         * @param {Array<string>} userIds The users to resolve for. Repeat the parameter for each id (&#x60;?userIds&#x3D;a&amp;userIds&#x3D;b&#x60;). Ids outside the current organization are omitted from the response. 
         * @param {string} startDate First day to resolve availability for
         * @param {string} [endDate] Last day to resolve availability for (inclusive). Defaults to startDate.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getUsersAvailability(userIds: Array<string>, startDate: string, endDate?: string, options?: RawAxiosRequestConfig): AxiosPromise<UsersAvailabilityResponse> {
            return localVarFp.getUsersAvailability(userIds, startDate, endDate, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * AvailabilityApi - object-oriented interface
 */
export class AvailabilityApi extends BaseAPI {
    /**
     * Resolves the authenticated user\'s free/busy availability over a date range: the working blocks for each weekday, minus availability exceptions, minus booked appointments. When `endDate` is omitted a single day (`startDate`) is returned. 
     * @summary Get current user\'s resolved availability
     * @param {string} startDate First day to resolve availability for
     * @param {string} [endDate] Last day to resolve availability for (inclusive). Defaults to startDate.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getMyAvailability(startDate: string, endDate?: string, options?: RawAxiosRequestConfig) {
        return AvailabilityApiFp(this.configuration).getMyAvailability(startDate, endDate, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Resolves another user\'s free/busy availability over a date range within the current organization. See `getMyAvailability` for the resolution rules.  Reading a colleague\'s availability is a scheduling action, not user administration: it requires `appointments.read` (the permission that gates the combined agenda) or `users.read`. 
     * @summary Get a user\'s resolved availability
     * @param {string} userId User ID
     * @param {string} startDate First day to resolve availability for
     * @param {string} [endDate] Last day to resolve availability for (inclusive). Defaults to startDate.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getUserAvailability(userId: string, startDate: string, endDate?: string, options?: RawAxiosRequestConfig) {
        return AvailabilityApiFp(this.configuration).getUserAvailability(userId, startDate, endDate, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Resolves the free/busy availability of every user in `userIds` over a date range within the current organization, in a single request. Backs the combined agenda\'s \"aanwezig vandaag\" filter and the appointment slot-availability hints, which previously fired one request per roster member. See `getUserAvailability` for the per-day resolution rules.  Reading colleagues\' availability is a scheduling action, not user administration: it requires `appointments.read` (the permission that gates the combined agenda) or `users.read`. Ids that are not members of the current organization are silently omitted from the response rather than failing the whole request. 
     * @summary Get resolved availability for several users at once
     * @param {Array<string>} userIds The users to resolve for. Repeat the parameter for each id (&#x60;?userIds&#x3D;a&amp;userIds&#x3D;b&#x60;). Ids outside the current organization are omitted from the response. 
     * @param {string} startDate First day to resolve availability for
     * @param {string} [endDate] Last day to resolve availability for (inclusive). Defaults to startDate.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getUsersAvailability(userIds: Array<string>, startDate: string, endDate?: string, options?: RawAxiosRequestConfig) {
        return AvailabilityApiFp(this.configuration).getUsersAvailability(userIds, startDate, endDate, options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * UsersApi - axios parameter creator
 */
export const UsersApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * Returns the profile of the currently authenticated user
         * @summary Get current user profile
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getCurrentUser: async (options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/api/v1/users/me`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * The permissions this request was authorised with, in the active organization: the user\'s roles there, filtered by the organization\'s license (ADR-023). Exactly what every `@PreAuthorize` check on this request answers from, so a screen that hides what is not listed here hides exactly what the API would refuse.  Empty when the user has no active organization or their membership in it is disabled. An impersonated session answers for the organization it is pinned to. 
         * @summary List the current user\'s effective permissions
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getMyPermissions: async (options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/api/v1/users/me/permissions`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Returns a user profile by ID (must be in the same organization)
         * @summary Get user by ID
         * @param {string} userId User ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getUserById: async (userId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'userId' is not null or undefined
            assertParamExists('getUserById', 'userId', userId)
            const localVarPath = `/api/v1/users/{userId}`
                .replace(`{${"userId"}}`, encodeURIComponent(String(userId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Returns every active member the organization has designated as behandelaar (`is_behandelaar` on the membership, #2791), ordered by last name.  This is the roster a behandelteam picker and a zorgtraject\'s behandelaar pickers are built from, and it is deliberately **not** `GET /api/v1/users`: that endpoint answers user administration and requires `users.read`, which the default Behandelaar role does not hold — so the picker came back empty for the very people it exists for, with no error anywhere (#4156). Building a behandelteam is a normal behandelaar action, so this reads on `clients.read` as well, and answers with a projection that carries no user-management data (no email, phone, AGB code, BIG registration or preferences).  Unpaged on purpose: an organization\'s behandelaars are a bounded set that a picker shows whole, and cutting it into pages would make \"the pool is empty\" unanswerable. 
         * @summary List the organization\'s designated behandelaars
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listBehandelaars: async (options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/api/v1/users/behandelaars`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Returns the beroepen zorgverlener valid today, read straight off the committed Vektis publication (`CL0001-VEKT`, *\"beroepen die geen AGB-code kunnen aanvragen\"*).  This is what the behandelaar form\'s picker is filled from. It is an endpoint rather than a generated enum because the same publication also bounds what may be stored (`beroepZorgverlener`), and two copies of a code table drift — here, into a picker offering a code the API refuses, or an API accepting one no practice can choose.  Reference data with nothing organization-specific in it, so authentication is the only gate. 
         * @summary List the Vektis CL0001-VEKT beroepen a behandelaar can declare
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listBeroepenZorgverlener: async (options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/api/v1/users/beroepen-zorgverlener`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Returns all organizations the authenticated user is a member of. Used for populating the organization switcher dropdown. 
         * @summary List organizations the current user belongs to
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listMyOrganizations: async (options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/api/v1/users/me/organizations`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Returns a paginated list of users in the authenticated user\'s organization
         * @summary List users in organization
         * @param {number} [page] Page number (0-indexed)
         * @param {number} [size] Page size
         * @param {string} [sort] Sort specification as \&quot;field,direction\&quot; (e.g., \&quot;lastName,asc\&quot; or \&quot;createdAt,desc\&quot;). Supported fields: lastName, firstName, email, isActive, isBehandelaar, createdAt, updatedAt, lastSignedInAt. Any other field falls back to lastName.
         * @param {string} [search] Search query (searches firstName, lastName, email)
         * @param {boolean} [isActive] Filter by active status
         * @param {boolean} [isRegiebehandelaar] Filter by regiebehandelaar designation. Set to &#x60;true&#x60; to list only the organization\&#39;s designated regiebehandelaren, which feeds the zorgtraject regiebehandelaar picker. 
         * @param {number} [notSignedInForDays] Keep only the members whose last sign-in is longer ago than this many days, so a practice can find the seats it pays for and nobody uses (ADR-042 §3).  An account that has **never** signed in matches every value: an invited account that was never used is the most likely unused seat of all, and excluding it would hide the clearest case from the filter that exists to find it.  Reads &#x60;lastSignedInAt&#x60;, so the caveats on that property apply here too — it is last authentication rather than last activity, and it is per user rather than per membership. 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listUsers: async (page?: number, size?: number, sort?: string, search?: string, isActive?: boolean, isRegiebehandelaar?: boolean, notSignedInForDays?: number, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/api/v1/users`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (page !== undefined) {
                localVarQueryParameter['page'] = page;
            }

            if (size !== undefined) {
                localVarQueryParameter['size'] = size;
            }

            if (sort !== undefined) {
                localVarQueryParameter['sort'] = sort;
            }

            if (search !== undefined) {
                localVarQueryParameter['search'] = search;
            }

            if (isActive !== undefined) {
                localVarQueryParameter['isActive'] = isActive;
            }

            if (isRegiebehandelaar !== undefined) {
                localVarQueryParameter['isRegiebehandelaar'] = isRegiebehandelaar;
            }

            if (notSignedInForDays !== undefined) {
                localVarQueryParameter['notSignedInForDays'] = notSignedInForDays;
            }

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Switches the user\'s active organization. Updates Firebase custom claims with the new organization\'s permissions. The client must call `getIdToken(true)` after this endpoint to get a refreshed token with the new claims. 
         * @summary Switch to a different organization
         * @param {SwitchOrganizationRequest} switchOrganizationRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        switchActiveOrganization: async (switchOrganizationRequest: SwitchOrganizationRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'switchOrganizationRequest' is not null or undefined
            assertParamExists('switchActiveOrganization', 'switchOrganizationRequest', switchOrganizationRequest)
            const localVarPath = `/api/v1/users/me/active-organization`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(switchOrganizationRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Synchronizes the user\'s Firebase custom claims with their current database state. This updates organizationId and permissions in Firebase to match what\'s in the database. Useful for fixing inconsistent state or after organization changes. 
         * @summary Sync Firebase custom claims with database state
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        syncFirebaseClaims: async (options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/api/v1/users/me/sync-firebase-claims`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Updates the profile of the currently authenticated user.  `isActive` may not be switched off here: disabling your own membership would leave you unable to reach the organisation at all, and nothing but user administration could undo it. That is a `409`. 
         * @summary Update current user profile
         * @param {UpdateUserProfileRequest} updateUserProfileRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateCurrentUser: async (updateUserProfileRequest: UpdateUserProfileRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'updateUserProfileRequest' is not null or undefined
            assertParamExists('updateCurrentUser', 'updateUserProfileRequest', updateUserProfileRequest)
            const localVarPath = `/api/v1/users/me`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(updateUserProfileRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Updates a user profile by ID (requires admin privileges).  Switching `isActive` off is refused with a `409` when it would lock the organisation out of its own user administration — when the membership is your own, or when it is the last enabled one holding `users.update`. Disabling a membership also clears its `isBehandelaar` and `isRegiebehandelaar` designations; re-enabling does not restore them. 
         * @summary Update user by ID
         * @param {string} userId User ID
         * @param {UpdateUserProfileRequest} updateUserProfileRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateUserById: async (userId: string, updateUserProfileRequest: UpdateUserProfileRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'userId' is not null or undefined
            assertParamExists('updateUserById', 'userId', userId)
            // verify required parameter 'updateUserProfileRequest' is not null or undefined
            assertParamExists('updateUserById', 'updateUserProfileRequest', updateUserProfileRequest)
            const localVarPath = `/api/v1/users/{userId}`
                .replace(`{${"userId"}}`, encodeURIComponent(String(userId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(updateUserProfileRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * UsersApi - functional programming interface
 */
export const UsersApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = UsersApiAxiosParamCreator(configuration)
    return {
        /**
         * Returns the profile of the currently authenticated user
         * @summary Get current user profile
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getCurrentUser(options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<UserProfile>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getCurrentUser(options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['UsersApi.getCurrentUser']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * The permissions this request was authorised with, in the active organization: the user\'s roles there, filtered by the organization\'s license (ADR-023). Exactly what every `@PreAuthorize` check on this request answers from, so a screen that hides what is not listed here hides exactly what the API would refuse.  Empty when the user has no active organization or their membership in it is disabled. An impersonated session answers for the organization it is pinned to. 
         * @summary List the current user\'s effective permissions
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getMyPermissions(options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<CurrentUserPermissions>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getMyPermissions(options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['UsersApi.getMyPermissions']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Returns a user profile by ID (must be in the same organization)
         * @summary Get user by ID
         * @param {string} userId User ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getUserById(userId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<UserProfile>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getUserById(userId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['UsersApi.getUserById']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Returns every active member the organization has designated as behandelaar (`is_behandelaar` on the membership, #2791), ordered by last name.  This is the roster a behandelteam picker and a zorgtraject\'s behandelaar pickers are built from, and it is deliberately **not** `GET /api/v1/users`: that endpoint answers user administration and requires `users.read`, which the default Behandelaar role does not hold — so the picker came back empty for the very people it exists for, with no error anywhere (#4156). Building a behandelteam is a normal behandelaar action, so this reads on `clients.read` as well, and answers with a projection that carries no user-management data (no email, phone, AGB code, BIG registration or preferences).  Unpaged on purpose: an organization\'s behandelaars are a bounded set that a picker shows whole, and cutting it into pages would make \"the pool is empty\" unanswerable. 
         * @summary List the organization\'s designated behandelaars
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listBehandelaars(options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Array<BehandelaarSummary>>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listBehandelaars(options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['UsersApi.listBehandelaars']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Returns the beroepen zorgverlener valid today, read straight off the committed Vektis publication (`CL0001-VEKT`, *\"beroepen die geen AGB-code kunnen aanvragen\"*).  This is what the behandelaar form\'s picker is filled from. It is an endpoint rather than a generated enum because the same publication also bounds what may be stored (`beroepZorgverlener`), and two copies of a code table drift — here, into a picker offering a code the API refuses, or an API accepting one no practice can choose.  Reference data with nothing organization-specific in it, so authentication is the only gate. 
         * @summary List the Vektis CL0001-VEKT beroepen a behandelaar can declare
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listBeroepenZorgverlener(options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Array<BeroepZorgverlener>>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listBeroepenZorgverlener(options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['UsersApi.listBeroepenZorgverlener']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Returns all organizations the authenticated user is a member of. Used for populating the organization switcher dropdown. 
         * @summary List organizations the current user belongs to
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listMyOrganizations(options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<UserOrganizationsResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listMyOrganizations(options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['UsersApi.listMyOrganizations']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Returns a paginated list of users in the authenticated user\'s organization
         * @summary List users in organization
         * @param {number} [page] Page number (0-indexed)
         * @param {number} [size] Page size
         * @param {string} [sort] Sort specification as \&quot;field,direction\&quot; (e.g., \&quot;lastName,asc\&quot; or \&quot;createdAt,desc\&quot;). Supported fields: lastName, firstName, email, isActive, isBehandelaar, createdAt, updatedAt, lastSignedInAt. Any other field falls back to lastName.
         * @param {string} [search] Search query (searches firstName, lastName, email)
         * @param {boolean} [isActive] Filter by active status
         * @param {boolean} [isRegiebehandelaar] Filter by regiebehandelaar designation. Set to &#x60;true&#x60; to list only the organization\&#39;s designated regiebehandelaren, which feeds the zorgtraject regiebehandelaar picker. 
         * @param {number} [notSignedInForDays] Keep only the members whose last sign-in is longer ago than this many days, so a practice can find the seats it pays for and nobody uses (ADR-042 §3).  An account that has **never** signed in matches every value: an invited account that was never used is the most likely unused seat of all, and excluding it would hide the clearest case from the filter that exists to find it.  Reads &#x60;lastSignedInAt&#x60;, so the caveats on that property apply here too — it is last authentication rather than last activity, and it is per user rather than per membership. 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listUsers(page?: number, size?: number, sort?: string, search?: string, isActive?: boolean, isRegiebehandelaar?: boolean, notSignedInForDays?: number, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<UserListResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listUsers(page, size, sort, search, isActive, isRegiebehandelaar, notSignedInForDays, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['UsersApi.listUsers']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Switches the user\'s active organization. Updates Firebase custom claims with the new organization\'s permissions. The client must call `getIdToken(true)` after this endpoint to get a refreshed token with the new claims. 
         * @summary Switch to a different organization
         * @param {SwitchOrganizationRequest} switchOrganizationRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async switchActiveOrganization(switchOrganizationRequest: SwitchOrganizationRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<SwitchOrganizationResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.switchActiveOrganization(switchOrganizationRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['UsersApi.switchActiveOrganization']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Synchronizes the user\'s Firebase custom claims with their current database state. This updates organizationId and permissions in Firebase to match what\'s in the database. Useful for fixing inconsistent state or after organization changes. 
         * @summary Sync Firebase custom claims with database state
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async syncFirebaseClaims(options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<SyncFirebaseClaims200Response>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.syncFirebaseClaims(options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['UsersApi.syncFirebaseClaims']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Updates the profile of the currently authenticated user.  `isActive` may not be switched off here: disabling your own membership would leave you unable to reach the organisation at all, and nothing but user administration could undo it. That is a `409`. 
         * @summary Update current user profile
         * @param {UpdateUserProfileRequest} updateUserProfileRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async updateCurrentUser(updateUserProfileRequest: UpdateUserProfileRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<UserProfile>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.updateCurrentUser(updateUserProfileRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['UsersApi.updateCurrentUser']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Updates a user profile by ID (requires admin privileges).  Switching `isActive` off is refused with a `409` when it would lock the organisation out of its own user administration — when the membership is your own, or when it is the last enabled one holding `users.update`. Disabling a membership also clears its `isBehandelaar` and `isRegiebehandelaar` designations; re-enabling does not restore them. 
         * @summary Update user by ID
         * @param {string} userId User ID
         * @param {UpdateUserProfileRequest} updateUserProfileRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async updateUserById(userId: string, updateUserProfileRequest: UpdateUserProfileRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<UserProfile>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.updateUserById(userId, updateUserProfileRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['UsersApi.updateUserById']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * UsersApi - factory interface
 */
export const UsersApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = UsersApiFp(configuration)
    return {
        /**
         * Returns the profile of the currently authenticated user
         * @summary Get current user profile
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getCurrentUser(options?: RawAxiosRequestConfig): AxiosPromise<UserProfile> {
            return localVarFp.getCurrentUser(options).then((request) => request(axios, basePath));
        },
        /**
         * The permissions this request was authorised with, in the active organization: the user\'s roles there, filtered by the organization\'s license (ADR-023). Exactly what every `@PreAuthorize` check on this request answers from, so a screen that hides what is not listed here hides exactly what the API would refuse.  Empty when the user has no active organization or their membership in it is disabled. An impersonated session answers for the organization it is pinned to. 
         * @summary List the current user\'s effective permissions
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getMyPermissions(options?: RawAxiosRequestConfig): AxiosPromise<CurrentUserPermissions> {
            return localVarFp.getMyPermissions(options).then((request) => request(axios, basePath));
        },
        /**
         * Returns a user profile by ID (must be in the same organization)
         * @summary Get user by ID
         * @param {string} userId User ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getUserById(userId: string, options?: RawAxiosRequestConfig): AxiosPromise<UserProfile> {
            return localVarFp.getUserById(userId, options).then((request) => request(axios, basePath));
        },
        /**
         * Returns every active member the organization has designated as behandelaar (`is_behandelaar` on the membership, #2791), ordered by last name.  This is the roster a behandelteam picker and a zorgtraject\'s behandelaar pickers are built from, and it is deliberately **not** `GET /api/v1/users`: that endpoint answers user administration and requires `users.read`, which the default Behandelaar role does not hold — so the picker came back empty for the very people it exists for, with no error anywhere (#4156). Building a behandelteam is a normal behandelaar action, so this reads on `clients.read` as well, and answers with a projection that carries no user-management data (no email, phone, AGB code, BIG registration or preferences).  Unpaged on purpose: an organization\'s behandelaars are a bounded set that a picker shows whole, and cutting it into pages would make \"the pool is empty\" unanswerable. 
         * @summary List the organization\'s designated behandelaars
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listBehandelaars(options?: RawAxiosRequestConfig): AxiosPromise<Array<BehandelaarSummary>> {
            return localVarFp.listBehandelaars(options).then((request) => request(axios, basePath));
        },
        /**
         * Returns the beroepen zorgverlener valid today, read straight off the committed Vektis publication (`CL0001-VEKT`, *\"beroepen die geen AGB-code kunnen aanvragen\"*).  This is what the behandelaar form\'s picker is filled from. It is an endpoint rather than a generated enum because the same publication also bounds what may be stored (`beroepZorgverlener`), and two copies of a code table drift — here, into a picker offering a code the API refuses, or an API accepting one no practice can choose.  Reference data with nothing organization-specific in it, so authentication is the only gate. 
         * @summary List the Vektis CL0001-VEKT beroepen a behandelaar can declare
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listBeroepenZorgverlener(options?: RawAxiosRequestConfig): AxiosPromise<Array<BeroepZorgverlener>> {
            return localVarFp.listBeroepenZorgverlener(options).then((request) => request(axios, basePath));
        },
        /**
         * Returns all organizations the authenticated user is a member of. Used for populating the organization switcher dropdown. 
         * @summary List organizations the current user belongs to
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listMyOrganizations(options?: RawAxiosRequestConfig): AxiosPromise<UserOrganizationsResponse> {
            return localVarFp.listMyOrganizations(options).then((request) => request(axios, basePath));
        },
        /**
         * Returns a paginated list of users in the authenticated user\'s organization
         * @summary List users in organization
         * @param {number} [page] Page number (0-indexed)
         * @param {number} [size] Page size
         * @param {string} [sort] Sort specification as \&quot;field,direction\&quot; (e.g., \&quot;lastName,asc\&quot; or \&quot;createdAt,desc\&quot;). Supported fields: lastName, firstName, email, isActive, isBehandelaar, createdAt, updatedAt, lastSignedInAt. Any other field falls back to lastName.
         * @param {string} [search] Search query (searches firstName, lastName, email)
         * @param {boolean} [isActive] Filter by active status
         * @param {boolean} [isRegiebehandelaar] Filter by regiebehandelaar designation. Set to &#x60;true&#x60; to list only the organization\&#39;s designated regiebehandelaren, which feeds the zorgtraject regiebehandelaar picker. 
         * @param {number} [notSignedInForDays] Keep only the members whose last sign-in is longer ago than this many days, so a practice can find the seats it pays for and nobody uses (ADR-042 §3).  An account that has **never** signed in matches every value: an invited account that was never used is the most likely unused seat of all, and excluding it would hide the clearest case from the filter that exists to find it.  Reads &#x60;lastSignedInAt&#x60;, so the caveats on that property apply here too — it is last authentication rather than last activity, and it is per user rather than per membership. 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listUsers(page?: number, size?: number, sort?: string, search?: string, isActive?: boolean, isRegiebehandelaar?: boolean, notSignedInForDays?: number, options?: RawAxiosRequestConfig): AxiosPromise<UserListResponse> {
            return localVarFp.listUsers(page, size, sort, search, isActive, isRegiebehandelaar, notSignedInForDays, options).then((request) => request(axios, basePath));
        },
        /**
         * Switches the user\'s active organization. Updates Firebase custom claims with the new organization\'s permissions. The client must call `getIdToken(true)` after this endpoint to get a refreshed token with the new claims. 
         * @summary Switch to a different organization
         * @param {SwitchOrganizationRequest} switchOrganizationRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        switchActiveOrganization(switchOrganizationRequest: SwitchOrganizationRequest, options?: RawAxiosRequestConfig): AxiosPromise<SwitchOrganizationResponse> {
            return localVarFp.switchActiveOrganization(switchOrganizationRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Synchronizes the user\'s Firebase custom claims with their current database state. This updates organizationId and permissions in Firebase to match what\'s in the database. Useful for fixing inconsistent state or after organization changes. 
         * @summary Sync Firebase custom claims with database state
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        syncFirebaseClaims(options?: RawAxiosRequestConfig): AxiosPromise<SyncFirebaseClaims200Response> {
            return localVarFp.syncFirebaseClaims(options).then((request) => request(axios, basePath));
        },
        /**
         * Updates the profile of the currently authenticated user.  `isActive` may not be switched off here: disabling your own membership would leave you unable to reach the organisation at all, and nothing but user administration could undo it. That is a `409`. 
         * @summary Update current user profile
         * @param {UpdateUserProfileRequest} updateUserProfileRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateCurrentUser(updateUserProfileRequest: UpdateUserProfileRequest, options?: RawAxiosRequestConfig): AxiosPromise<UserProfile> {
            return localVarFp.updateCurrentUser(updateUserProfileRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Updates a user profile by ID (requires admin privileges).  Switching `isActive` off is refused with a `409` when it would lock the organisation out of its own user administration — when the membership is your own, or when it is the last enabled one holding `users.update`. Disabling a membership also clears its `isBehandelaar` and `isRegiebehandelaar` designations; re-enabling does not restore them. 
         * @summary Update user by ID
         * @param {string} userId User ID
         * @param {UpdateUserProfileRequest} updateUserProfileRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateUserById(userId: string, updateUserProfileRequest: UpdateUserProfileRequest, options?: RawAxiosRequestConfig): AxiosPromise<UserProfile> {
            return localVarFp.updateUserById(userId, updateUserProfileRequest, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * UsersApi - object-oriented interface
 */
export class UsersApi extends BaseAPI {
    /**
     * Returns the profile of the currently authenticated user
     * @summary Get current user profile
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getCurrentUser(options?: RawAxiosRequestConfig) {
        return UsersApiFp(this.configuration).getCurrentUser(options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * The permissions this request was authorised with, in the active organization: the user\'s roles there, filtered by the organization\'s license (ADR-023). Exactly what every `@PreAuthorize` check on this request answers from, so a screen that hides what is not listed here hides exactly what the API would refuse.  Empty when the user has no active organization or their membership in it is disabled. An impersonated session answers for the organization it is pinned to. 
     * @summary List the current user\'s effective permissions
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getMyPermissions(options?: RawAxiosRequestConfig) {
        return UsersApiFp(this.configuration).getMyPermissions(options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Returns a user profile by ID (must be in the same organization)
     * @summary Get user by ID
     * @param {string} userId User ID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getUserById(userId: string, options?: RawAxiosRequestConfig) {
        return UsersApiFp(this.configuration).getUserById(userId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Returns every active member the organization has designated as behandelaar (`is_behandelaar` on the membership, #2791), ordered by last name.  This is the roster a behandelteam picker and a zorgtraject\'s behandelaar pickers are built from, and it is deliberately **not** `GET /api/v1/users`: that endpoint answers user administration and requires `users.read`, which the default Behandelaar role does not hold — so the picker came back empty for the very people it exists for, with no error anywhere (#4156). Building a behandelteam is a normal behandelaar action, so this reads on `clients.read` as well, and answers with a projection that carries no user-management data (no email, phone, AGB code, BIG registration or preferences).  Unpaged on purpose: an organization\'s behandelaars are a bounded set that a picker shows whole, and cutting it into pages would make \"the pool is empty\" unanswerable. 
     * @summary List the organization\'s designated behandelaars
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public listBehandelaars(options?: RawAxiosRequestConfig) {
        return UsersApiFp(this.configuration).listBehandelaars(options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Returns the beroepen zorgverlener valid today, read straight off the committed Vektis publication (`CL0001-VEKT`, *\"beroepen die geen AGB-code kunnen aanvragen\"*).  This is what the behandelaar form\'s picker is filled from. It is an endpoint rather than a generated enum because the same publication also bounds what may be stored (`beroepZorgverlener`), and two copies of a code table drift — here, into a picker offering a code the API refuses, or an API accepting one no practice can choose.  Reference data with nothing organization-specific in it, so authentication is the only gate. 
     * @summary List the Vektis CL0001-VEKT beroepen a behandelaar can declare
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public listBeroepenZorgverlener(options?: RawAxiosRequestConfig) {
        return UsersApiFp(this.configuration).listBeroepenZorgverlener(options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Returns all organizations the authenticated user is a member of. Used for populating the organization switcher dropdown. 
     * @summary List organizations the current user belongs to
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public listMyOrganizations(options?: RawAxiosRequestConfig) {
        return UsersApiFp(this.configuration).listMyOrganizations(options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Returns a paginated list of users in the authenticated user\'s organization
     * @summary List users in organization
     * @param {number} [page] Page number (0-indexed)
     * @param {number} [size] Page size
     * @param {string} [sort] Sort specification as \&quot;field,direction\&quot; (e.g., \&quot;lastName,asc\&quot; or \&quot;createdAt,desc\&quot;). Supported fields: lastName, firstName, email, isActive, isBehandelaar, createdAt, updatedAt, lastSignedInAt. Any other field falls back to lastName.
     * @param {string} [search] Search query (searches firstName, lastName, email)
     * @param {boolean} [isActive] Filter by active status
     * @param {boolean} [isRegiebehandelaar] Filter by regiebehandelaar designation. Set to &#x60;true&#x60; to list only the organization\&#39;s designated regiebehandelaren, which feeds the zorgtraject regiebehandelaar picker. 
     * @param {number} [notSignedInForDays] Keep only the members whose last sign-in is longer ago than this many days, so a practice can find the seats it pays for and nobody uses (ADR-042 §3).  An account that has **never** signed in matches every value: an invited account that was never used is the most likely unused seat of all, and excluding it would hide the clearest case from the filter that exists to find it.  Reads &#x60;lastSignedInAt&#x60;, so the caveats on that property apply here too — it is last authentication rather than last activity, and it is per user rather than per membership. 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public listUsers(page?: number, size?: number, sort?: string, search?: string, isActive?: boolean, isRegiebehandelaar?: boolean, notSignedInForDays?: number, options?: RawAxiosRequestConfig) {
        return UsersApiFp(this.configuration).listUsers(page, size, sort, search, isActive, isRegiebehandelaar, notSignedInForDays, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Switches the user\'s active organization. Updates Firebase custom claims with the new organization\'s permissions. The client must call `getIdToken(true)` after this endpoint to get a refreshed token with the new claims. 
     * @summary Switch to a different organization
     * @param {SwitchOrganizationRequest} switchOrganizationRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public switchActiveOrganization(switchOrganizationRequest: SwitchOrganizationRequest, options?: RawAxiosRequestConfig) {
        return UsersApiFp(this.configuration).switchActiveOrganization(switchOrganizationRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Synchronizes the user\'s Firebase custom claims with their current database state. This updates organizationId and permissions in Firebase to match what\'s in the database. Useful for fixing inconsistent state or after organization changes. 
     * @summary Sync Firebase custom claims with database state
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public syncFirebaseClaims(options?: RawAxiosRequestConfig) {
        return UsersApiFp(this.configuration).syncFirebaseClaims(options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Updates the profile of the currently authenticated user.  `isActive` may not be switched off here: disabling your own membership would leave you unable to reach the organisation at all, and nothing but user administration could undo it. That is a `409`. 
     * @summary Update current user profile
     * @param {UpdateUserProfileRequest} updateUserProfileRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public updateCurrentUser(updateUserProfileRequest: UpdateUserProfileRequest, options?: RawAxiosRequestConfig) {
        return UsersApiFp(this.configuration).updateCurrentUser(updateUserProfileRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Updates a user profile by ID (requires admin privileges).  Switching `isActive` off is refused with a `409` when it would lock the organisation out of its own user administration — when the membership is your own, or when it is the last enabled one holding `users.update`. Disabling a membership also clears its `isBehandelaar` and `isRegiebehandelaar` designations; re-enabling does not restore them. 
     * @summary Update user by ID
     * @param {string} userId User ID
     * @param {UpdateUserProfileRequest} updateUserProfileRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public updateUserById(userId: string, updateUserProfileRequest: UpdateUserProfileRequest, options?: RawAxiosRequestConfig) {
        return UsersApiFp(this.configuration).updateUserById(userId, updateUserProfileRequest, options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * WorkingHoursApi - axios parameter creator
 */
export const WorkingHoursApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * 
         * @summary Create an availability exception for the current user
         * @param {AvailabilityExceptionRequest} availabilityExceptionRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createMyAvailabilityException: async (availabilityExceptionRequest: AvailabilityExceptionRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'availabilityExceptionRequest' is not null or undefined
            assertParamExists('createMyAvailabilityException', 'availabilityExceptionRequest', availabilityExceptionRequest)
            const localVarPath = `/api/v1/users/me/availability-exceptions`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(availabilityExceptionRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Requires user-management permission.
         * @summary Create an availability exception for a user
         * @param {string} userId User ID
         * @param {AvailabilityExceptionRequest} availabilityExceptionRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createUserAvailabilityException: async (userId: string, availabilityExceptionRequest: AvailabilityExceptionRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'userId' is not null or undefined
            assertParamExists('createUserAvailabilityException', 'userId', userId)
            // verify required parameter 'availabilityExceptionRequest' is not null or undefined
            assertParamExists('createUserAvailabilityException', 'availabilityExceptionRequest', availabilityExceptionRequest)
            const localVarPath = `/api/v1/users/{userId}/availability-exceptions`
                .replace(`{${"userId"}}`, encodeURIComponent(String(userId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(availabilityExceptionRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Delete one of the current user\'s availability exceptions
         * @param {string} exceptionId Availability exception ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteMyAvailabilityException: async (exceptionId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'exceptionId' is not null or undefined
            assertParamExists('deleteMyAvailabilityException', 'exceptionId', exceptionId)
            const localVarPath = `/api/v1/users/me/availability-exceptions/{exceptionId}`
                .replace(`{${"exceptionId"}}`, encodeURIComponent(String(exceptionId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Requires user-management permission.
         * @summary Delete a user\'s availability exception
         * @param {string} userId User ID
         * @param {string} exceptionId Availability exception ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteUserAvailabilityException: async (userId: string, exceptionId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'userId' is not null or undefined
            assertParamExists('deleteUserAvailabilityException', 'userId', userId)
            // verify required parameter 'exceptionId' is not null or undefined
            assertParamExists('deleteUserAvailabilityException', 'exceptionId', exceptionId)
            const localVarPath = `/api/v1/users/{userId}/availability-exceptions/{exceptionId}`
                .replace(`{${"userId"}}`, encodeURIComponent(String(userId)))
                .replace(`{${"exceptionId"}}`, encodeURIComponent(String(exceptionId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Returns the authenticated user\'s weekly working-hours pattern for the current organization. Every weekday MONDAY..SUNDAY is present; a weekday with an empty `blocks` list means the user does not work that day. 
         * @summary Get current user\'s working hours
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getMyWorkingHours: async (options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/api/v1/users/me/working-hours`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Returns another user\'s weekly working-hours pattern within the current organization. Requires user-management permission when the target is not the authenticated user. 
         * @summary Get a user\'s working hours
         * @param {string} userId User ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getUserWorkingHours: async (userId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'userId' is not null or undefined
            assertParamExists('getUserWorkingHours', 'userId', userId)
            const localVarPath = `/api/v1/users/{userId}/working-hours`
                .replace(`{${"userId"}}`, encodeURIComponent(String(userId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Returns the weekly working-hours pattern of every user in `userIds` within the current organization, in a single request. Backs the combined agenda\'s per-behandelaar working-day shading, which previously fired one request per visible column (#3253).  Reading colleagues\' working hours to shade an agenda is a scheduling action, not user administration: it requires `appointments.read` (the permission that gates the combined agenda) or `users.read` — the same gate as `getUsersAvailability`. Ids that are not members of the current organization are silently omitted from the response rather than failing the whole request. 
         * @summary Get the working hours of several users at once
         * @param {Array<string>} userIds The users to resolve for. Repeat the parameter for each id (&#x60;?userIds&#x3D;a&amp;userIds&#x3D;b&#x60;). Ids outside the current organization are omitted from the response. 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getUsersWorkingHours: async (userIds: Array<string>, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'userIds' is not null or undefined
            assertParamExists('getUsersWorkingHours', 'userIds', userIds)
            const localVarPath = `/api/v1/users/working-hours`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (userIds) {
                localVarQueryParameter['userIds'] = userIds;
            }

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Returns the authenticated user\'s availability exceptions (holidays, incidental absences, adjusted hours) for the current organization, optionally bounded to a date range. 
         * @summary List current user\'s availability exceptions
         * @param {string} [from] Only return exceptions ending on or after this date (inclusive)
         * @param {string} [to] Only return exceptions starting on or before this date (inclusive)
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listMyAvailabilityExceptions: async (from?: string, to?: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/api/v1/users/me/availability-exceptions`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (from !== undefined) {
                localVarQueryParameter['from'] = (from as any instanceof Date) ?
                    (from as any).toISOString().substring(0,10) :
                    from;
            }

            if (to !== undefined) {
                localVarQueryParameter['to'] = (to as any instanceof Date) ?
                    (to as any).toISOString().substring(0,10) :
                    to;
            }

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Returns another user\'s availability exceptions within the current organization. Requires user-management permission. 
         * @summary List a user\'s availability exceptions
         * @param {string} userId User ID
         * @param {string} [from] Only return exceptions ending on or after this date (inclusive)
         * @param {string} [to] Only return exceptions starting on or before this date (inclusive)
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listUserAvailabilityExceptions: async (userId: string, from?: string, to?: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'userId' is not null or undefined
            assertParamExists('listUserAvailabilityExceptions', 'userId', userId)
            const localVarPath = `/api/v1/users/{userId}/availability-exceptions`
                .replace(`{${"userId"}}`, encodeURIComponent(String(userId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (from !== undefined) {
                localVarQueryParameter['from'] = (from as any instanceof Date) ?
                    (from as any).toISOString().substring(0,10) :
                    from;
            }

            if (to !== undefined) {
                localVarQueryParameter['to'] = (to as any instanceof Date) ?
                    (to as any).toISOString().substring(0,10) :
                    to;
            }

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Update one of the current user\'s availability exceptions
         * @param {string} exceptionId Availability exception ID
         * @param {AvailabilityExceptionRequest} availabilityExceptionRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateMyAvailabilityException: async (exceptionId: string, availabilityExceptionRequest: AvailabilityExceptionRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'exceptionId' is not null or undefined
            assertParamExists('updateMyAvailabilityException', 'exceptionId', exceptionId)
            // verify required parameter 'availabilityExceptionRequest' is not null or undefined
            assertParamExists('updateMyAvailabilityException', 'availabilityExceptionRequest', availabilityExceptionRequest)
            const localVarPath = `/api/v1/users/me/availability-exceptions/{exceptionId}`
                .replace(`{${"exceptionId"}}`, encodeURIComponent(String(exceptionId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(availabilityExceptionRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Replaces the authenticated user\'s full weekly working-hours pattern for the current organization. Weekdays omitted from the request are treated as non-working days (no blocks). 
         * @summary Replace current user\'s working hours
         * @param {UpdateWorkingHoursRequest} updateWorkingHoursRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateMyWorkingHours: async (updateWorkingHoursRequest: UpdateWorkingHoursRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'updateWorkingHoursRequest' is not null or undefined
            assertParamExists('updateMyWorkingHours', 'updateWorkingHoursRequest', updateWorkingHoursRequest)
            const localVarPath = `/api/v1/users/me/working-hours`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(updateWorkingHoursRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Requires user-management permission.
         * @summary Update a user\'s availability exception
         * @param {string} userId User ID
         * @param {string} exceptionId Availability exception ID
         * @param {AvailabilityExceptionRequest} availabilityExceptionRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateUserAvailabilityException: async (userId: string, exceptionId: string, availabilityExceptionRequest: AvailabilityExceptionRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'userId' is not null or undefined
            assertParamExists('updateUserAvailabilityException', 'userId', userId)
            // verify required parameter 'exceptionId' is not null or undefined
            assertParamExists('updateUserAvailabilityException', 'exceptionId', exceptionId)
            // verify required parameter 'availabilityExceptionRequest' is not null or undefined
            assertParamExists('updateUserAvailabilityException', 'availabilityExceptionRequest', availabilityExceptionRequest)
            const localVarPath = `/api/v1/users/{userId}/availability-exceptions/{exceptionId}`
                .replace(`{${"userId"}}`, encodeURIComponent(String(userId)))
                .replace(`{${"exceptionId"}}`, encodeURIComponent(String(exceptionId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(availabilityExceptionRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Replaces another user\'s weekly working-hours pattern within the current organization. Requires user-management permission. 
         * @summary Replace a user\'s working hours
         * @param {string} userId User ID
         * @param {UpdateWorkingHoursRequest} updateWorkingHoursRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateUserWorkingHours: async (userId: string, updateWorkingHoursRequest: UpdateWorkingHoursRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'userId' is not null or undefined
            assertParamExists('updateUserWorkingHours', 'userId', userId)
            // verify required parameter 'updateWorkingHoursRequest' is not null or undefined
            assertParamExists('updateUserWorkingHours', 'updateWorkingHoursRequest', updateWorkingHoursRequest)
            const localVarPath = `/api/v1/users/{userId}/working-hours`
                .replace(`{${"userId"}}`, encodeURIComponent(String(userId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(updateWorkingHoursRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * WorkingHoursApi - functional programming interface
 */
export const WorkingHoursApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = WorkingHoursApiAxiosParamCreator(configuration)
    return {
        /**
         * 
         * @summary Create an availability exception for the current user
         * @param {AvailabilityExceptionRequest} availabilityExceptionRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async createMyAvailabilityException(availabilityExceptionRequest: AvailabilityExceptionRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<AvailabilityException>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.createMyAvailabilityException(availabilityExceptionRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['WorkingHoursApi.createMyAvailabilityException']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Requires user-management permission.
         * @summary Create an availability exception for a user
         * @param {string} userId User ID
         * @param {AvailabilityExceptionRequest} availabilityExceptionRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async createUserAvailabilityException(userId: string, availabilityExceptionRequest: AvailabilityExceptionRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<AvailabilityException>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.createUserAvailabilityException(userId, availabilityExceptionRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['WorkingHoursApi.createUserAvailabilityException']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Delete one of the current user\'s availability exceptions
         * @param {string} exceptionId Availability exception ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async deleteMyAvailabilityException(exceptionId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.deleteMyAvailabilityException(exceptionId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['WorkingHoursApi.deleteMyAvailabilityException']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Requires user-management permission.
         * @summary Delete a user\'s availability exception
         * @param {string} userId User ID
         * @param {string} exceptionId Availability exception ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async deleteUserAvailabilityException(userId: string, exceptionId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.deleteUserAvailabilityException(userId, exceptionId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['WorkingHoursApi.deleteUserAvailabilityException']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Returns the authenticated user\'s weekly working-hours pattern for the current organization. Every weekday MONDAY..SUNDAY is present; a weekday with an empty `blocks` list means the user does not work that day. 
         * @summary Get current user\'s working hours
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getMyWorkingHours(options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<WorkingHours>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getMyWorkingHours(options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['WorkingHoursApi.getMyWorkingHours']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Returns another user\'s weekly working-hours pattern within the current organization. Requires user-management permission when the target is not the authenticated user. 
         * @summary Get a user\'s working hours
         * @param {string} userId User ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getUserWorkingHours(userId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<WorkingHours>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getUserWorkingHours(userId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['WorkingHoursApi.getUserWorkingHours']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Returns the weekly working-hours pattern of every user in `userIds` within the current organization, in a single request. Backs the combined agenda\'s per-behandelaar working-day shading, which previously fired one request per visible column (#3253).  Reading colleagues\' working hours to shade an agenda is a scheduling action, not user administration: it requires `appointments.read` (the permission that gates the combined agenda) or `users.read` — the same gate as `getUsersAvailability`. Ids that are not members of the current organization are silently omitted from the response rather than failing the whole request. 
         * @summary Get the working hours of several users at once
         * @param {Array<string>} userIds The users to resolve for. Repeat the parameter for each id (&#x60;?userIds&#x3D;a&amp;userIds&#x3D;b&#x60;). Ids outside the current organization are omitted from the response. 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getUsersWorkingHours(userIds: Array<string>, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<UsersWorkingHoursResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getUsersWorkingHours(userIds, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['WorkingHoursApi.getUsersWorkingHours']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Returns the authenticated user\'s availability exceptions (holidays, incidental absences, adjusted hours) for the current organization, optionally bounded to a date range. 
         * @summary List current user\'s availability exceptions
         * @param {string} [from] Only return exceptions ending on or after this date (inclusive)
         * @param {string} [to] Only return exceptions starting on or before this date (inclusive)
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listMyAvailabilityExceptions(from?: string, to?: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<AvailabilityExceptionListResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listMyAvailabilityExceptions(from, to, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['WorkingHoursApi.listMyAvailabilityExceptions']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Returns another user\'s availability exceptions within the current organization. Requires user-management permission. 
         * @summary List a user\'s availability exceptions
         * @param {string} userId User ID
         * @param {string} [from] Only return exceptions ending on or after this date (inclusive)
         * @param {string} [to] Only return exceptions starting on or before this date (inclusive)
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listUserAvailabilityExceptions(userId: string, from?: string, to?: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<AvailabilityExceptionListResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listUserAvailabilityExceptions(userId, from, to, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['WorkingHoursApi.listUserAvailabilityExceptions']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Update one of the current user\'s availability exceptions
         * @param {string} exceptionId Availability exception ID
         * @param {AvailabilityExceptionRequest} availabilityExceptionRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async updateMyAvailabilityException(exceptionId: string, availabilityExceptionRequest: AvailabilityExceptionRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<AvailabilityException>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.updateMyAvailabilityException(exceptionId, availabilityExceptionRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['WorkingHoursApi.updateMyAvailabilityException']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Replaces the authenticated user\'s full weekly working-hours pattern for the current organization. Weekdays omitted from the request are treated as non-working days (no blocks). 
         * @summary Replace current user\'s working hours
         * @param {UpdateWorkingHoursRequest} updateWorkingHoursRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async updateMyWorkingHours(updateWorkingHoursRequest: UpdateWorkingHoursRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<WorkingHours>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.updateMyWorkingHours(updateWorkingHoursRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['WorkingHoursApi.updateMyWorkingHours']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Requires user-management permission.
         * @summary Update a user\'s availability exception
         * @param {string} userId User ID
         * @param {string} exceptionId Availability exception ID
         * @param {AvailabilityExceptionRequest} availabilityExceptionRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async updateUserAvailabilityException(userId: string, exceptionId: string, availabilityExceptionRequest: AvailabilityExceptionRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<AvailabilityException>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.updateUserAvailabilityException(userId, exceptionId, availabilityExceptionRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['WorkingHoursApi.updateUserAvailabilityException']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Replaces another user\'s weekly working-hours pattern within the current organization. Requires user-management permission. 
         * @summary Replace a user\'s working hours
         * @param {string} userId User ID
         * @param {UpdateWorkingHoursRequest} updateWorkingHoursRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async updateUserWorkingHours(userId: string, updateWorkingHoursRequest: UpdateWorkingHoursRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<WorkingHours>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.updateUserWorkingHours(userId, updateWorkingHoursRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['WorkingHoursApi.updateUserWorkingHours']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * WorkingHoursApi - factory interface
 */
export const WorkingHoursApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = WorkingHoursApiFp(configuration)
    return {
        /**
         * 
         * @summary Create an availability exception for the current user
         * @param {AvailabilityExceptionRequest} availabilityExceptionRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createMyAvailabilityException(availabilityExceptionRequest: AvailabilityExceptionRequest, options?: RawAxiosRequestConfig): AxiosPromise<AvailabilityException> {
            return localVarFp.createMyAvailabilityException(availabilityExceptionRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Requires user-management permission.
         * @summary Create an availability exception for a user
         * @param {string} userId User ID
         * @param {AvailabilityExceptionRequest} availabilityExceptionRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createUserAvailabilityException(userId: string, availabilityExceptionRequest: AvailabilityExceptionRequest, options?: RawAxiosRequestConfig): AxiosPromise<AvailabilityException> {
            return localVarFp.createUserAvailabilityException(userId, availabilityExceptionRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Delete one of the current user\'s availability exceptions
         * @param {string} exceptionId Availability exception ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteMyAvailabilityException(exceptionId: string, options?: RawAxiosRequestConfig): AxiosPromise<void> {
            return localVarFp.deleteMyAvailabilityException(exceptionId, options).then((request) => request(axios, basePath));
        },
        /**
         * Requires user-management permission.
         * @summary Delete a user\'s availability exception
         * @param {string} userId User ID
         * @param {string} exceptionId Availability exception ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteUserAvailabilityException(userId: string, exceptionId: string, options?: RawAxiosRequestConfig): AxiosPromise<void> {
            return localVarFp.deleteUserAvailabilityException(userId, exceptionId, options).then((request) => request(axios, basePath));
        },
        /**
         * Returns the authenticated user\'s weekly working-hours pattern for the current organization. Every weekday MONDAY..SUNDAY is present; a weekday with an empty `blocks` list means the user does not work that day. 
         * @summary Get current user\'s working hours
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getMyWorkingHours(options?: RawAxiosRequestConfig): AxiosPromise<WorkingHours> {
            return localVarFp.getMyWorkingHours(options).then((request) => request(axios, basePath));
        },
        /**
         * Returns another user\'s weekly working-hours pattern within the current organization. Requires user-management permission when the target is not the authenticated user. 
         * @summary Get a user\'s working hours
         * @param {string} userId User ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getUserWorkingHours(userId: string, options?: RawAxiosRequestConfig): AxiosPromise<WorkingHours> {
            return localVarFp.getUserWorkingHours(userId, options).then((request) => request(axios, basePath));
        },
        /**
         * Returns the weekly working-hours pattern of every user in `userIds` within the current organization, in a single request. Backs the combined agenda\'s per-behandelaar working-day shading, which previously fired one request per visible column (#3253).  Reading colleagues\' working hours to shade an agenda is a scheduling action, not user administration: it requires `appointments.read` (the permission that gates the combined agenda) or `users.read` — the same gate as `getUsersAvailability`. Ids that are not members of the current organization are silently omitted from the response rather than failing the whole request. 
         * @summary Get the working hours of several users at once
         * @param {Array<string>} userIds The users to resolve for. Repeat the parameter for each id (&#x60;?userIds&#x3D;a&amp;userIds&#x3D;b&#x60;). Ids outside the current organization are omitted from the response. 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getUsersWorkingHours(userIds: Array<string>, options?: RawAxiosRequestConfig): AxiosPromise<UsersWorkingHoursResponse> {
            return localVarFp.getUsersWorkingHours(userIds, options).then((request) => request(axios, basePath));
        },
        /**
         * Returns the authenticated user\'s availability exceptions (holidays, incidental absences, adjusted hours) for the current organization, optionally bounded to a date range. 
         * @summary List current user\'s availability exceptions
         * @param {string} [from] Only return exceptions ending on or after this date (inclusive)
         * @param {string} [to] Only return exceptions starting on or before this date (inclusive)
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listMyAvailabilityExceptions(from?: string, to?: string, options?: RawAxiosRequestConfig): AxiosPromise<AvailabilityExceptionListResponse> {
            return localVarFp.listMyAvailabilityExceptions(from, to, options).then((request) => request(axios, basePath));
        },
        /**
         * Returns another user\'s availability exceptions within the current organization. Requires user-management permission. 
         * @summary List a user\'s availability exceptions
         * @param {string} userId User ID
         * @param {string} [from] Only return exceptions ending on or after this date (inclusive)
         * @param {string} [to] Only return exceptions starting on or before this date (inclusive)
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listUserAvailabilityExceptions(userId: string, from?: string, to?: string, options?: RawAxiosRequestConfig): AxiosPromise<AvailabilityExceptionListResponse> {
            return localVarFp.listUserAvailabilityExceptions(userId, from, to, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Update one of the current user\'s availability exceptions
         * @param {string} exceptionId Availability exception ID
         * @param {AvailabilityExceptionRequest} availabilityExceptionRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateMyAvailabilityException(exceptionId: string, availabilityExceptionRequest: AvailabilityExceptionRequest, options?: RawAxiosRequestConfig): AxiosPromise<AvailabilityException> {
            return localVarFp.updateMyAvailabilityException(exceptionId, availabilityExceptionRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Replaces the authenticated user\'s full weekly working-hours pattern for the current organization. Weekdays omitted from the request are treated as non-working days (no blocks). 
         * @summary Replace current user\'s working hours
         * @param {UpdateWorkingHoursRequest} updateWorkingHoursRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateMyWorkingHours(updateWorkingHoursRequest: UpdateWorkingHoursRequest, options?: RawAxiosRequestConfig): AxiosPromise<WorkingHours> {
            return localVarFp.updateMyWorkingHours(updateWorkingHoursRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Requires user-management permission.
         * @summary Update a user\'s availability exception
         * @param {string} userId User ID
         * @param {string} exceptionId Availability exception ID
         * @param {AvailabilityExceptionRequest} availabilityExceptionRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateUserAvailabilityException(userId: string, exceptionId: string, availabilityExceptionRequest: AvailabilityExceptionRequest, options?: RawAxiosRequestConfig): AxiosPromise<AvailabilityException> {
            return localVarFp.updateUserAvailabilityException(userId, exceptionId, availabilityExceptionRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Replaces another user\'s weekly working-hours pattern within the current organization. Requires user-management permission. 
         * @summary Replace a user\'s working hours
         * @param {string} userId User ID
         * @param {UpdateWorkingHoursRequest} updateWorkingHoursRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateUserWorkingHours(userId: string, updateWorkingHoursRequest: UpdateWorkingHoursRequest, options?: RawAxiosRequestConfig): AxiosPromise<WorkingHours> {
            return localVarFp.updateUserWorkingHours(userId, updateWorkingHoursRequest, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * WorkingHoursApi - object-oriented interface
 */
export class WorkingHoursApi extends BaseAPI {
    /**
     * 
     * @summary Create an availability exception for the current user
     * @param {AvailabilityExceptionRequest} availabilityExceptionRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public createMyAvailabilityException(availabilityExceptionRequest: AvailabilityExceptionRequest, options?: RawAxiosRequestConfig) {
        return WorkingHoursApiFp(this.configuration).createMyAvailabilityException(availabilityExceptionRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Requires user-management permission.
     * @summary Create an availability exception for a user
     * @param {string} userId User ID
     * @param {AvailabilityExceptionRequest} availabilityExceptionRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public createUserAvailabilityException(userId: string, availabilityExceptionRequest: AvailabilityExceptionRequest, options?: RawAxiosRequestConfig) {
        return WorkingHoursApiFp(this.configuration).createUserAvailabilityException(userId, availabilityExceptionRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Delete one of the current user\'s availability exceptions
     * @param {string} exceptionId Availability exception ID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public deleteMyAvailabilityException(exceptionId: string, options?: RawAxiosRequestConfig) {
        return WorkingHoursApiFp(this.configuration).deleteMyAvailabilityException(exceptionId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Requires user-management permission.
     * @summary Delete a user\'s availability exception
     * @param {string} userId User ID
     * @param {string} exceptionId Availability exception ID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public deleteUserAvailabilityException(userId: string, exceptionId: string, options?: RawAxiosRequestConfig) {
        return WorkingHoursApiFp(this.configuration).deleteUserAvailabilityException(userId, exceptionId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Returns the authenticated user\'s weekly working-hours pattern for the current organization. Every weekday MONDAY..SUNDAY is present; a weekday with an empty `blocks` list means the user does not work that day. 
     * @summary Get current user\'s working hours
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getMyWorkingHours(options?: RawAxiosRequestConfig) {
        return WorkingHoursApiFp(this.configuration).getMyWorkingHours(options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Returns another user\'s weekly working-hours pattern within the current organization. Requires user-management permission when the target is not the authenticated user. 
     * @summary Get a user\'s working hours
     * @param {string} userId User ID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getUserWorkingHours(userId: string, options?: RawAxiosRequestConfig) {
        return WorkingHoursApiFp(this.configuration).getUserWorkingHours(userId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Returns the weekly working-hours pattern of every user in `userIds` within the current organization, in a single request. Backs the combined agenda\'s per-behandelaar working-day shading, which previously fired one request per visible column (#3253).  Reading colleagues\' working hours to shade an agenda is a scheduling action, not user administration: it requires `appointments.read` (the permission that gates the combined agenda) or `users.read` — the same gate as `getUsersAvailability`. Ids that are not members of the current organization are silently omitted from the response rather than failing the whole request. 
     * @summary Get the working hours of several users at once
     * @param {Array<string>} userIds The users to resolve for. Repeat the parameter for each id (&#x60;?userIds&#x3D;a&amp;userIds&#x3D;b&#x60;). Ids outside the current organization are omitted from the response. 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getUsersWorkingHours(userIds: Array<string>, options?: RawAxiosRequestConfig) {
        return WorkingHoursApiFp(this.configuration).getUsersWorkingHours(userIds, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Returns the authenticated user\'s availability exceptions (holidays, incidental absences, adjusted hours) for the current organization, optionally bounded to a date range. 
     * @summary List current user\'s availability exceptions
     * @param {string} [from] Only return exceptions ending on or after this date (inclusive)
     * @param {string} [to] Only return exceptions starting on or before this date (inclusive)
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public listMyAvailabilityExceptions(from?: string, to?: string, options?: RawAxiosRequestConfig) {
        return WorkingHoursApiFp(this.configuration).listMyAvailabilityExceptions(from, to, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Returns another user\'s availability exceptions within the current organization. Requires user-management permission. 
     * @summary List a user\'s availability exceptions
     * @param {string} userId User ID
     * @param {string} [from] Only return exceptions ending on or after this date (inclusive)
     * @param {string} [to] Only return exceptions starting on or before this date (inclusive)
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public listUserAvailabilityExceptions(userId: string, from?: string, to?: string, options?: RawAxiosRequestConfig) {
        return WorkingHoursApiFp(this.configuration).listUserAvailabilityExceptions(userId, from, to, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Update one of the current user\'s availability exceptions
     * @param {string} exceptionId Availability exception ID
     * @param {AvailabilityExceptionRequest} availabilityExceptionRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public updateMyAvailabilityException(exceptionId: string, availabilityExceptionRequest: AvailabilityExceptionRequest, options?: RawAxiosRequestConfig) {
        return WorkingHoursApiFp(this.configuration).updateMyAvailabilityException(exceptionId, availabilityExceptionRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Replaces the authenticated user\'s full weekly working-hours pattern for the current organization. Weekdays omitted from the request are treated as non-working days (no blocks). 
     * @summary Replace current user\'s working hours
     * @param {UpdateWorkingHoursRequest} updateWorkingHoursRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public updateMyWorkingHours(updateWorkingHoursRequest: UpdateWorkingHoursRequest, options?: RawAxiosRequestConfig) {
        return WorkingHoursApiFp(this.configuration).updateMyWorkingHours(updateWorkingHoursRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Requires user-management permission.
     * @summary Update a user\'s availability exception
     * @param {string} userId User ID
     * @param {string} exceptionId Availability exception ID
     * @param {AvailabilityExceptionRequest} availabilityExceptionRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public updateUserAvailabilityException(userId: string, exceptionId: string, availabilityExceptionRequest: AvailabilityExceptionRequest, options?: RawAxiosRequestConfig) {
        return WorkingHoursApiFp(this.configuration).updateUserAvailabilityException(userId, exceptionId, availabilityExceptionRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Replaces another user\'s weekly working-hours pattern within the current organization. Requires user-management permission. 
     * @summary Replace a user\'s working hours
     * @param {string} userId User ID
     * @param {UpdateWorkingHoursRequest} updateWorkingHoursRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public updateUserWorkingHours(userId: string, updateWorkingHoursRequest: UpdateWorkingHoursRequest, options?: RawAxiosRequestConfig) {
        return WorkingHoursApiFp(this.configuration).updateUserWorkingHours(userId, updateWorkingHoursRequest, options).then((request) => request(this.axios, this.basePath));
    }
}



